/**
 * V2MOM Editor
 *
 * One page, five sections: Vision, Values, Methods (each with its Obstacles and
 * Measures), plus obstacles and measures that span the whole plan. The right
 * column says whose document this is and which V2MOM it aligns to, shows the
 * parent's vision and methods so the author writes against them, the people
 * writing against this one (the cascade), and the record of what happened.
 *
 * Every field saves on blur. Only a draft is editable. The process (migration
 * 945): the author sends it for review to the owner of the V2MOM it aligns to;
 * that person approves it (published) or returns it with a note (draft); a
 * published V2MOM is locked for the period and reopened mid-period only when
 * the reviewer says so. The header draws only the acts this person may take
 * now; where the next move is someone else's, a plain line says whose.
 *
 * Route: /prioritization/strategy/v2mom/:v2momId
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlignVerticalJustifyStart,
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  Loader2,
  Plus,
  Printer,
  Send,
  Trash2,
} from 'lucide-react';
import { PageHeader } from '../../../../components/prioritization/PageHeader';
import { Callout, Checkbox, Disclosure, Field, Modal, Panel, Select, TextArea, TextInput } from '@/components/ds';
import { btnDanger, btnPrimary, btnSecondary, LABEL_MONO, status as statusReadout, type Tone } from '@/components/mono/primitives';
import { useV2momStore, type ActResult } from '../../../../stores/prioritization/v2momStore';
import { useTenantStore } from '../../../../stores/prioritization/tenantStore';
import { useUserStore } from '../../../../stores/prioritization/userStore';
import { useOrgUnits, useEnterpriseIdentityStore } from '../../../../stores/prioritization/enterpriseIdentityStore';
import { useWorkspaceMembers } from '../../../../hooks/useWorkspaceMembers';
import { listObjectives, type StrategicObjective } from '../../../../lib/strategyAlignment';
import { formatDate, formatDateTime } from '../../../../i18n/formatters';
import {
  alignmentChain,
  buildV2momTree,
  dueState,
  eligibleParents,
  isV2momEditable,
  unmeasuredMethods,
  v2momActions,
  v2momStanding,
  v2momWaitingLine,
  V2MOM_EVENT_LABEL,
  V2MOM_SECTIONS,
  V2MOM_STATUS_LABEL,
  v2momSectionFill,
  type V2mom,
  type V2momAction,
  type V2momEntry,
  type V2momEntryKind,
  type V2momMethodGroup,
  type V2momStatus,
} from '../../../../lib/v2mom/v2momModel';

const STATUS_TONE: Record<V2momStatus, Tone> = {
  draft: 'neutral',
  in_review: 'mustard',
  published: 'green',
  locked: 'slate',
};

const LINE_PLACEHOLDER: Record<V2momEntryKind, string> = {
  value: 'A value — e.g. "Customer trust before growth"',
  method: 'A method — e.g. "Launch the partner program in EMEA"',
  obstacle: 'An obstacle — e.g. "No EMEA sales headcount until Q3"',
  measure: 'A measure — e.g. "20 signed partners"',
};

// ── Field that saves on blur ───────────────────────────────────

function BlurText({
  value,
  onSave,
  placeholder,
  disabled,
  multiline,
  rows,
  ariaLabel,
}: {
  value: string | null;
  onSave: (next: string) => void;
  placeholder?: string;
  disabled?: boolean;
  multiline?: boolean;
  rows?: number;
  ariaLabel: string;
}) {
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);

  function commit() {
    if (draft !== (value ?? '')) onSave(draft);
  }

  if (multiline) {
    return (
      <TextArea
        aria-label={ariaLabel}
        value={draft}
        rows={rows ?? 3}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
      />
    );
  }
  return (
    <TextInput
      aria-label={ariaLabel}
      value={draft}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
    />
  );
}

// ── Add-a-line form ────────────────────────────────────────────

function AddLine({
  kind,
  onAdd,
  disabled,
}: {
  kind: V2momEntryKind;
  onAdd: (title: string) => Promise<void>;
  disabled?: boolean;
}) {
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit() {
    const t = title.trim();
    if (!t) return;
    setBusy(true);
    await onAdd(t);
    setBusy(false);
    setTitle('');
  }

  if (disabled) return null;
  return (
    <div className="flex items-center gap-2">
      <TextInput
        aria-label={`New ${kind}`}
        value={title}
        placeholder={LINE_PLACEHOLDER[kind]}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void submit();
        }}
      />
      <button {...btnSecondary()} onClick={submit} disabled={busy || !title.trim()}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
        Add
      </button>
    </div>
  );
}

// ── One line (value / obstacle / measure) ─────────────────────

function EntryLine({
  entry,
  index,
  count,
  editable,
  showTarget,
}: {
  entry: V2momEntry;
  index: number;
  count: number;
  editable: boolean;
  showTarget?: boolean;
}) {
  const updateEntry = useV2momStore((s) => s.updateEntry);
  const moveEntry = useV2momStore((s) => s.moveEntry);
  const deleteEntry = useV2momStore((s) => s.deleteEntry);

  return (
    <div className="flex items-start gap-2 py-1.5">
      <span className={`${LABEL_MONO} mt-2.5 w-6 flex-shrink-0 text-right`}>{index + 1}</span>
      <div className="min-w-0 flex-1 space-y-1.5">
        <BlurText
          ariaLabel={`${entry.kind} title`}
          value={entry.title}
          placeholder={LINE_PLACEHOLDER[entry.kind]}
          disabled={!editable}
          onSave={(title) => void updateEntry(entry.id, { title: title.trim() || entry.title })}
        />
        {showTarget && (
          <BlurText
            ariaLabel="Measure target"
            value={entry.target}
            placeholder={'Target — the bar this is held to, e.g. "$4M ARR by Q4"'}
            disabled={!editable}
            onSave={(target) => void updateEntry(entry.id, { target: target.trim() || null })}
          />
        )}
      </div>
      {editable && (
        <div className="flex flex-shrink-0 items-center gap-1">
          <button {...btnSecondary('h-9 w-9 px-0')} title="Move up" disabled={index === 0} onClick={() => void moveEntry(entry.id, 'up')}>
            <ArrowUp className="h-3.5 w-3.5" />
          </button>
          <button {...btnSecondary('h-9 w-9 px-0')} title="Move down" disabled={index === count - 1} onClick={() => void moveEntry(entry.id, 'down')}>
            <ArrowDown className="h-3.5 w-3.5" />
          </button>
          <button {...btnDanger('h-9 w-9 px-0')} title="Remove" onClick={() => void deleteEntry(entry.id)}>
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

function EntryList({
  v2momId,
  kind,
  methodId,
  entries,
  editable,
  emptyText,
}: {
  v2momId: string;
  kind: 'value' | 'obstacle' | 'measure';
  methodId?: string | null;
  entries: V2momEntry[];
  editable: boolean;
  emptyText: string;
}) {
  const createEntry = useV2momStore((s) => s.createEntry);
  return (
    <div className="space-y-1">
      {entries.length === 0 && <p className="py-1.5 text-sm text-[var(--text-ter)]">{emptyText}</p>}
      {entries.map((e, i) => (
        <EntryLine key={e.id} entry={e} index={i} count={entries.length} editable={editable} showTarget={kind === 'measure'} />
      ))}
      <AddLine
        kind={kind}
        disabled={!editable}
        onAdd={async (title) => {
          await createEntry(v2momId, { kind, title, methodId: methodId ?? null });
        }}
      />
    </div>
  );
}

// ── A method with its obstacles and measures ──────────────────

function MethodBlock({
  v2momId,
  group,
  index,
  count,
  editable,
  objectives,
}: {
  v2momId: string;
  group: V2momMethodGroup;
  index: number;
  count: number;
  editable: boolean;
  objectives: StrategicObjective[];
}) {
  const updateEntry = useV2momStore((s) => s.updateEntry);
  const moveEntry = useV2momStore((s) => s.moveEntry);
  const deleteEntry = useV2momStore((s) => s.deleteEntry);
  const { method, obstacles, measures } = group;
  const objective = method.objectiveId ? objectives.find((o) => o.id === method.objectiveId) : undefined;

  const meta = [
    `${obstacles.length} obstacle${obstacles.length === 1 ? '' : 's'}`,
    measures.length === 0 ? 'no measure yet' : `${measures.length} measure${measures.length === 1 ? '' : 's'}`,
  ].join(' · ');

  function confirmDelete() {
    const n = obstacles.length + measures.length;
    const msg = n
      ? `Remove this method and the ${n} obstacle${n === 1 ? '' : 's'}/measure${n === 1 ? '' : 's'} under it?`
      : 'Remove this method?';
    if (window.confirm(msg)) void deleteEntry(method.id);
  }

  return (
    <Disclosure
      title={
        <span className="inline-flex items-center gap-2">
          <span className={LABEL_MONO}>{index + 1}</span>
          <span className="font-heading font-semibold">{method.title || 'Untitled method'}</span>
        </span>
      }
      meta={meta}
      accent={measures.length === 0 ? 'coral' : 'slate'}
      defaultOpen={index === 0}
    >
      <div className="space-y-4">
        <Field label="Method">
          <BlurText
            ariaLabel="Method title"
            value={method.title}
            placeholder={LINE_PLACEHOLDER.method}
            disabled={!editable}
            onSave={(title) => void updateEntry(method.id, { title: title.trim() || method.title })}
          />
        </Field>
        <Field label="How" hint="What doing this looks like — enough for the people who report to you to write their own methods against it.">
          <BlurText
            ariaLabel="Method detail"
            value={method.detail}
            multiline
            rows={3}
            placeholder="Describe the method…"
            disabled={!editable}
            onSave={(detail) => void updateEntry(method.id, { detail: detail.trim() || null })}
          />
        </Field>
        <Field
          label="Delivers objective"
          hint={objective ? undefined : 'Optional. Ties this method to a Strategic Objective so alignment reports can see it.'}
        >
          <Select
            aria-label="Strategic objective"
            value={method.objectiveId ?? ''}
            disabled={!editable}
            placeholder="No objective linked"
            options={objectives.map((o) => ({ value: o.id, label: o.name }))}
            onChange={(e) => void updateEntry(method.id, { objectiveId: e.target.value || null })}
          />
        </Field>

        <div className="grid grid-cols-1 gap-4 2xl:grid-cols-2">
          <Panel label="Obstacles" title="What is in the way" inset>
            <EntryList v2momId={v2momId} kind="obstacle" methodId={method.id} entries={obstacles} editable={editable} emptyText="No obstacles named for this method." />
          </Panel>
          <Panel label="Measures" title="How we know it worked" inset accent={measures.length === 0 ? 'coral' : undefined}>
            <EntryList v2momId={v2momId} kind="measure" methodId={method.id} entries={measures} editable={editable} emptyText="Every method needs at least one measure." />
          </Panel>
        </div>

        {editable && (
          <div className="flex items-center justify-end gap-1 border-t pt-3" style={{ borderColor: 'var(--rule)' }}>
            <button {...btnSecondary()} disabled={index === 0} onClick={() => void moveEntry(method.id, 'up')}>
              <ArrowUp className="h-3.5 w-3.5" /> Up
            </button>
            <button {...btnSecondary()} disabled={index === count - 1} onClick={() => void moveEntry(method.id, 'down')}>
              <ArrowDown className="h-3.5 w-3.5" /> Down
            </button>
            <button {...btnDanger()} onClick={confirmDelete}>
              <Trash2 className="h-3.5 w-3.5" /> Remove method
            </button>
          </div>
        )}
      </div>
    </Disclosure>
  );
}

// ── An act that takes a note ───────────────────────────────────

function ActDialog({
  action,
  busy,
  onConfirm,
  onClose,
}: {
  action: V2momAction;
  busy: boolean;
  onConfirm: (note: string) => void;
  onClose: () => void;
}) {
  const [note, setNote] = useState('');
  const ready = !busy && (!action.needsNote || note.trim().length > 0);
  return (
    <Modal
      open
      onClose={onClose}
      title={action.label}
      label="V2MOM"
      description={action.needsNote ? 'A note is required. The person on the other end reads it.' : 'The person on the other end reads the note, if you leave one.'}
      size="sm"
      footer={
        <>
          <button {...btnSecondary()} onClick={onClose} disabled={busy}>Cancel</button>
          <button {...(action.tone === 'danger' ? btnDanger() : btnPrimary())} onClick={() => onConfirm(note.trim())} disabled={!ready}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            {action.label}
          </button>
        </>
      }
    >
      <Field label={action.noteLabel ?? 'Note'}>
        <TextArea aria-label={action.noteLabel ?? 'Note'} rows={4} value={note} onChange={(e) => setNote(e.target.value)} autoFocus />
      </Field>
    </Modal>
  );
}

// ── Ask people to write theirs ─────────────────────────────────

function AskDialog({
  parent,
  members,
  openAskedIds,
  onClose,
}: {
  parent: V2mom;
  members: { userId: string; name: string; email: string }[];
  openAskedIds: Set<string>;
  onClose: () => void;
}) {
  const askFor = useV2momStore((s) => s.askFor);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [dueOn, setDueOn] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const candidates = members.filter((m) => m.userId !== parent.ownerId);

  async function send() {
    setBusy(true);
    setError(null);
    const res = await askFor({ parentId: parent.id, userIds: [...picked], dueOn: dueOn || null, note: note.trim() || null });
    setBusy(false);
    if (!res.success) {
      setError(res.error ?? 'Could not send the asks.');
      return;
    }
    const skippedWritten = res.skipped.filter((s) => s.reason === 'already_written').length;
    const skippedOther = res.skipped.length - skippedWritten;
    setResult(
      [
        `Asked ${res.asked} ${res.asked === 1 ? 'person' : 'people'}.`,
        skippedWritten ? `${skippedWritten} already wrote theirs against this one.` : null,
        skippedOther ? `${skippedOther} not asked (not a member).` : null,
      ]
        .filter(Boolean)
        .join(' '),
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Ask people to write theirs"
      label="Cascade"
      description={`Each person is told, and “Write your V2MOM against ‘${parent.title}’” appears in what needs them until they send theirs for review.`}
      footer={
        result ? (
          <button {...btnPrimary()} onClick={onClose}>Done</button>
        ) : (
          <>
            <button {...btnSecondary()} onClick={onClose} disabled={busy}>Cancel</button>
            <button {...btnPrimary()} onClick={() => void send()} disabled={busy || picked.size === 0}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Ask {picked.size || ''} {picked.size === 1 ? 'person' : 'people'}
            </button>
          </>
        )
      }
    >
      {result ? (
        <Callout tone="green" title="Sent">{result}</Callout>
      ) : (
        <div className="space-y-4">
          {error && <Callout tone="coral" title="Not sent">{error}</Callout>}
          <Field label="Who" hint={candidates.length ? 'Members of this workspace. People already asked are marked; asking again moves their due date.' : 'Nobody else is in this workspace yet.'}>
            <div className="max-h-64 space-y-1 overflow-y-auto border p-2" style={{ borderColor: 'var(--rule)' }}>
              {candidates.map((m) => (
                <Checkbox
                  key={m.userId}
                  checked={picked.has(m.userId)}
                  onChange={(e) => {
                    const next = new Set(picked);
                    if (e.target.checked) next.add(m.userId);
                    else next.delete(m.userId);
                    setPicked(next);
                  }}
                  label={m.name}
                  description={[m.email, openAskedIds.has(m.userId) ? 'already asked' : null].filter(Boolean).join(' · ')}
                />
              ))}
            </div>
          </Field>
          <Field label="Due" hint="The day their V2MOM should be sent for review. Overdue asks nudge them the day after and weekly.">
            <TextInput type="date" aria-label="Due date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} />
          </Field>
          <Field label="Note" hint="Optional. Goes with the ask.">
            <TextArea aria-label="Note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Bring it to the offsite; three to five methods." />
          </Field>
        </div>
      )}
    </Modal>
  );
}

// ── Page ────────────────────────────────────────────────────────

export function V2momEditor() {
  const { v2momId } = useParams<{ v2momId: string }>();
  const navigate = useNavigate();
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const userId = useUserStore((s) => s.userId);
  const isAdmin = useUserStore((s) => s.can('admin.settings'));

  const v2moms = useV2momStore((s) => s.v2moms);
  const entriesByDoc = useV2momStore((s) => s.entriesByDoc);
  const asks = useV2momStore((s) => s.asks);
  const eventsByDoc = useV2momStore((s) => s.eventsByDoc);
  const isLoading = useV2momStore((s) => s.isLoading);
  const fetchV2moms = useV2momStore((s) => s.fetchV2moms);
  const fetchEvents = useV2momStore((s) => s.fetchEvents);
  const updateV2mom = useV2momStore((s) => s.updateV2mom);
  const deleteV2mom = useV2momStore((s) => s.deleteV2mom);
  const createEntry = useV2momStore((s) => s.createEntry);
  const withdrawAsk = useV2momStore((s) => s.withdrawAsk);
  const store = useV2momStore;

  const orgUnits = useOrgUnits();
  const fetchOrgUnits = useEnterpriseIdentityStore((s) => s.fetchFromSupabase);
  const { activeMembers, nameOf } = useWorkspaceMembers(workspaceId);

  const [objectives, setObjectives] = useState<StrategicObjective[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [actBusy, setActBusy] = useState(false);
  const [noteFor, setNoteFor] = useState<V2momAction | null>(null);
  const [asking, setAsking] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;
    void fetchV2moms();
    void fetchOrgUnits({ workspaceId });
    void listObjectives(workspaceId).then(setObjectives);
  }, [workspaceId, fetchV2moms, fetchOrgUnits]);

  useEffect(() => {
    if (v2momId) void fetchEvents(v2momId);
  }, [v2momId, fetchEvents]);

  const v2mom: V2mom | undefined = v2moms.find((d) => d.id === v2momId);
  const entries = useMemo(() => (v2momId ? entriesByDoc[v2momId] ?? [] : []), [entriesByDoc, v2momId]);
  const tree = useMemo(() => buildV2momTree(entries), [entries]);
  const standing = useMemo(
    () => (v2mom ? v2momStanding(v2mom, v2moms, userId || null, isAdmin) : null),
    [v2mom, v2moms, userId, isAdmin],
  );
  const editable = Boolean(v2mom && standing && isV2momEditable(v2mom) && standing.canManage);
  const actions = useMemo(() => (v2mom && standing ? v2momActions(v2mom, standing) : []), [v2mom, standing]);
  const headerActions = actions.filter((a) => a.key !== 'ask' && a.key !== 'delete');
  const mayAsk = actions.some((a) => a.key === 'ask');
  const mayDelete = actions.some((a) => a.key === 'delete');
  const waitingLine = v2mom && standing ? v2momWaitingLine(v2mom, standing, nameOf) : null;

  const parents = useMemo(() => (v2mom ? eligibleParents(v2mom, v2moms) : []), [v2mom, v2moms]);
  const chain = useMemo(() => (v2mom ? alignmentChain(v2mom, v2moms) : []), [v2mom, v2moms]);
  const parent = chain[1];
  const parentTree = useMemo(() => (parent ? buildV2momTree(entriesByDoc[parent.id] ?? []) : null), [parent, entriesByDoc]);
  const children = useMemo(() => v2moms.filter((d) => d.parentId === v2momId), [v2moms, v2momId]);
  const asksFromHere = useMemo(() => asks.filter((a) => a.parentV2momId === v2momId), [asks, v2momId]);
  const openAskedIds = useMemo(() => new Set(asksFromHere.filter((a) => a.status === 'open').map((a) => a.askedUserId)), [asksFromHere]);
  const events = v2momId ? eventsByDoc[v2momId] ?? [] : [];
  const fill = v2mom ? v2momSectionFill(v2mom, entries) : null;
  const missingMeasures = useMemo(() => unmeasuredMethods(entries), [entries]);
  const due = v2mom ? dueState(v2mom.dueOn) : null;

  const save = useCallback(
    async (updates: Parameters<typeof updateV2mom>[1]) => {
      if (!v2momId) return;
      setSaveError(null);
      const { error } = await updateV2mom(v2momId, updates);
      if (error) setSaveError(error);
    },
    [v2momId, updateV2mom],
  );

  async function perform(action: V2momAction, note: string | null) {
    if (!v2momId) return;
    if (action.key === 'submit' && missingMeasures.length > 0) {
      const ok = window.confirm(
        `${missingMeasures.length} method${missingMeasures.length === 1 ? ' has' : 's have'} no measure. ${action.label} anyway?`,
      );
      if (!ok) return;
    }
    setActBusy(true);
    setSaveError(null);
    const s = store.getState();
    let res: ActResult;
    switch (action.key) {
      case 'submit': res = await s.submit(v2momId, note); break;
      case 'withdraw_submission': res = await s.withdrawSubmission(v2momId); break;
      case 'approve': res = await s.approve(v2momId, note); break;
      case 'return': res = await s.returnToAuthor(v2momId, note ?? ''); break;
      case 'lock': res = await s.lock(v2momId); break;
      case 'request_reopen': res = await s.requestReopen(v2momId, note ?? ''); break;
      case 'reopen': res = await s.reopen(v2momId, note); break;
      case 'decline_reopen': res = await s.declineReopen(v2momId, note); break;
      default: res = { success: true };
    }
    setActBusy(false);
    setNoteFor(null);
    if (!res.success) setSaveError(res.error ?? 'The change did not save.');
  }

  function onAction(action: V2momAction) {
    if (action.key === 'ask') {
      setAsking(true);
      return;
    }
    if (action.needsNote || action.takesNote) {
      setNoteFor(action);
      return;
    }
    void perform(action, null);
  }

  async function handleDelete() {
    if (!v2mom) return;
    const n = children.length;
    const msg =
      `Delete "${v2mom.title}"?\n\nIts values, methods, obstacles and measures go with it, and so do the asks sent from it.` +
      (n ? `\n\n${n} V2MOM${n === 1 ? '' : 's'} align to this one and will lose the link.` : '') +
      '\n\nThis cannot be undone.';
    if (!window.confirm(msg)) return;
    const { error } = await deleteV2mom(v2mom.id);
    if (error) setSaveError(error);
    else navigate('/prioritization/strategy/v2mom');
  }

  if (!v2mom) {
    if (isLoading || v2moms.length === 0) {
      return (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-[var(--text-ter)]" />
        </div>
      );
    }
    return (
      <div className="space-y-4">
        <Callout tone="coral" title="V2MOM not found">This V2MOM is not in the current workspace.</Callout>
        <Link to="/prioritization/strategy/v2mom" {...btnSecondary()}>
          <ChevronLeft className="h-4 w-4" /> All V2MOMs
        </Link>
      </div>
    );
  }

  const orgUnitOptions = orgUnits
    .filter((u) => !u.workspaceId || u.workspaceId === workspaceId)
    .map((u) => ({ value: u.id, label: `${'  '.repeat(u.depth ?? 0)}${u.displayName || u.name}` }));

  const canSetDue = Boolean(standing?.canManage && (editable || standing?.isAdmin));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-3">
        <button {...btnSecondary('mt-1 h-9 w-9 px-0')} title="All V2MOMs" onClick={() => navigate('/prioritization/strategy/v2mom')}>
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="min-w-0 flex-1">
          <PageHeader
            icon={<AlignVerticalJustifyStart className="h-4 w-4 text-[var(--text-ter)]" />}
            iconBg="bg-[var(--slate-dim)]"
            title={v2mom.title}
            subtitle={
              <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
                <span {...statusReadout(STATUS_TONE[v2mom.status])}>{V2MOM_STATUS_LABEL[v2mom.status]}</span>
                {due && v2mom.status === 'draft' && <span {...statusReadout(due.overdue ? 'coral' : 'neutral')}>{due.label}</span>}
                {v2mom.periodLabel && <span>{v2mom.periodLabel}</span>}
                <span>{v2mom.ownerId ? `${nameOf(v2mom.ownerId)}’s` : 'No owner'}</span>
                <span>{parent ? `Aligns to ${parent.title}` : 'Top of its chain'}</span>
                <span>Updated {formatDate(v2mom.updatedAt)}</span>
              </span>
            }
            actions={
              <div className="flex flex-wrap items-center gap-2">
                {headerActions.map((a) => (
                  <button
                    key={a.key}
                    {...(a.tone === 'primary' ? btnPrimary() : a.tone === 'danger' ? btnDanger() : btnSecondary())}
                    disabled={actBusy}
                    onClick={() => onAction(a)}
                  >
                    {actBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                    {a.label}
                  </button>
                ))}
                <Link to={`/prioritization/strategy/v2mom/${v2mom.id}/print`} {...btnSecondary()} title="Paper-sized print preview">
                  <Printer className="h-3.5 w-3.5" /> Print
                </Link>
                {mayDelete && (
                  <button {...btnDanger()} onClick={() => void handleDelete()} title="Delete V2MOM">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            }
          />
        </div>
      </div>

      {saveError && (
        <Callout tone="coral" title="That did not go through">{saveError}</Callout>
      )}

      {/* Where it is in the process */}
      {v2mom.status === 'in_review' && (
        <Callout tone="mustard" title={`Sent for review${v2mom.submittedAt ? ` on ${formatDate(v2mom.submittedAt)}` : ''}`}>
          {waitingLine ?? `${nameOf(v2mom.submittedBy ?? v2mom.ownerId)} sent it to you. Accept it, or return it with what to change.`}
        </Callout>
      )}
      {v2mom.status === 'draft' && v2mom.reviewNote && v2mom.reviewedBy && (
        <Callout tone="mustard" title={`Returned by ${nameOf(v2mom.reviewedBy)}${v2mom.reviewedAt ? ` on ${formatDate(v2mom.reviewedAt)}` : ''}`}>
          {v2mom.reviewNote}
        </Callout>
      )}
      {v2mom.status === 'draft' && !v2mom.reviewNote && waitingLine && (
        <Callout tone="neutral" title="Draft">{waitingLine}</Callout>
      )}
      {(v2mom.status === 'published' || v2mom.status === 'locked') && (
        <Callout
          tone={v2mom.status === 'published' ? 'green' : 'slate'}
          title={
            v2mom.status === 'published'
              ? `Published${v2mom.publishedAt ? ` ${formatDate(v2mom.publishedAt)}` : ''}${v2mom.publishedBy ? ` by ${nameOf(v2mom.publishedBy)}` : ''}`
              : `Locked for the period${v2mom.lockedAt ? ` since ${formatDate(v2mom.lockedAt)}` : ''}`
          }
        >
          {v2mom.reopenRequestedAt
            ? waitingLine ?? `${nameOf(v2mom.reopenRequestedBy)} asked to reopen it${v2mom.reopenNote ? `: “${v2mom.reopenNote}”` : '.'} Reopen it, or keep it as it is.`
            : waitingLine ?? (v2mom.status === 'published'
                ? 'Readable by the organization as written. Lock it to freeze it for the period; to change it, reopen it.'
                : 'Frozen for the period. To change it, reopen it.')}
        </Callout>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Main column */}
        <div className="space-y-6 lg:col-span-2">
          <Panel label="Title" title="What this V2MOM is called">
            <BlurText
              ariaLabel="V2MOM title"
              value={v2mom.title}
              placeholder="e.g. FY27 — Product Engineering"
              disabled={!editable}
              onSave={(title) => void save({ title: title.trim() || 'Untitled V2MOM' })}
            />
          </Panel>

          <Panel label="Vision" title="What do we want?">
            <BlurText
              ariaLabel="Vision"
              value={v2mom.vision}
              multiline
              rows={4}
              placeholder="One or two sentences. The picture of the future this plan is for."
              disabled={!editable}
              onSave={(vision) => void save({ vision: vision.trim() || null })}
            />
          </Panel>

          <Panel label="Values" title="What is important to us as we pursue it?" count={tree.values.length}>
            <EntryList v2momId={v2mom.id} kind="value" entries={tree.values} editable={editable} emptyText="No values yet. Three to five is usual." />
          </Panel>

          <Panel label="Methods" title="How do we get it done?" count={tree.methods.length} flush>
            <div className="divide-y" style={{ borderColor: 'var(--rule)' }}>
              {tree.methods.length === 0 && (
                <p className="px-4 py-4 text-sm text-[var(--text-ter)]">No methods yet. Each one gets its own obstacles and measures.</p>
              )}
              {tree.methods.map((g, i) => (
                <MethodBlock
                  key={g.method.id}
                  v2momId={v2mom.id}
                  group={g}
                  index={i}
                  count={tree.methods.length}
                  editable={editable}
                  objectives={objectives}
                />
              ))}
              {editable && (
                <div className="px-4 py-3">
                  <AddLine
                    kind="method"
                    onAdd={async (title) => {
                      await createEntry(v2mom.id, { kind: 'method', title });
                    }}
                  />
                </div>
              )}
            </div>
          </Panel>

          <div className="grid grid-cols-1 gap-6 2xl:grid-cols-2">
            <Panel label="Obstacles" title="In the way of the whole plan" count={tree.obstacles.length}>
              <EntryList v2momId={v2mom.id} kind="obstacle" entries={tree.obstacles} editable={editable} emptyText="Obstacles that are not about one method go here." />
            </Panel>
            <Panel label="Measures" title="For the whole plan" count={tree.measures.length}>
              <EntryList v2momId={v2mom.id} kind="measure" entries={tree.measures} editable={editable} emptyText="Measures that span every method go here." />
            </Panel>
          </div>
        </div>

        {/* Side column */}
        <div className="space-y-6">
          <Panel label="Alignment" title="Whose page, written against which, due when">
            <div className="space-y-4">
              <Field label="Aligns to" hint={parents.length ? 'The V2MOM this one answers to — usually your manager’s. Its owner reviews this one.' : 'No other V2MOM to align to yet.'}>
                <Select
                  aria-label="Parent V2MOM"
                  value={v2mom.parentId ?? ''}
                  disabled={!editable || parents.length === 0}
                  placeholder="Top of the chain"
                  options={parents.map((p) => ({ value: p.id, label: v2moms.find((d) => d.id === p.id)?.title ?? p.id }))}
                  onChange={(e) => void save({ parentId: e.target.value || null })}
                />
              </Field>
              <div className="space-y-1 text-sm">
                <p className={LABEL_MONO}>Reviewer</p>
                <p className="text-[var(--text-sec)]">
                  {standing?.reviewerId
                    ? `${nameOf(standing.reviewerId)} — the owner of the V2MOM this one aligns to.`
                    : parent
                      ? 'The V2MOM this one aligns to has no owner, so this one publishes without a review.'
                      : 'Top of its chain: it publishes without a review.'}
                </p>
              </div>
              <Field label="Owner">
                <Select
                  aria-label="Owner"
                  value={v2mom.ownerId ?? ''}
                  disabled={!(standing?.isAdmin) && !editable}
                  placeholder="Nobody"
                  options={activeMembers.map((m) => ({ value: m.userId, label: m.name }))}
                  onChange={(e) => void save({ ownerId: e.target.value || null })}
                />
              </Field>
              <Field label="Org unit">
                <Select
                  aria-label="Org unit"
                  value={v2mom.orgUnitId ?? ''}
                  disabled={!editable}
                  placeholder="Whole workspace"
                  options={orgUnitOptions}
                  onChange={(e) => void save({ orgUnitId: e.target.value || null })}
                />
              </Field>
              <Field label="Period">
                <BlurText
                  ariaLabel="Period"
                  value={v2mom.periodLabel}
                  placeholder="e.g. FY27 or H1 2027"
                  disabled={!editable}
                  onSave={(periodLabel) => void save({ periodLabel: periodLabel.trim() || null })}
                />
              </Field>
              <Field label="Due" hint={due ? due.label : 'The day this is due to be sent for review.'}>
                <TextInput
                  type="date"
                  aria-label="Due date"
                  value={v2mom.dueOn ?? ''}
                  disabled={!canSetDue}
                  onChange={(e) => void save({ dueOn: e.target.value || null })}
                />
              </Field>

              {chain.length > 1 && (
                <div className="space-y-1">
                  <p className={LABEL_MONO}>Chain</p>
                  <ol className="space-y-1 text-sm">
                    {[...chain].reverse().map((d, i) => (
                      <li key={d.id} style={{ paddingLeft: `${i * 12}px` }}>
                        {d.id === v2mom.id ? (
                          <span className="font-semibold text-[var(--text-pri)]">{d.title}</span>
                        ) : (
                          <Link to={`/prioritization/strategy/v2mom/${d.id}`} className="text-[var(--text-ter)] hover:underline">
                            {d.title}
                          </Link>
                        )}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          </Panel>

          {parent && parentTree && (
            <Panel label="Written against" title={parent.title}>
              <div className="space-y-3 text-sm">
                <div>
                  <p className={LABEL_MONO}>Vision</p>
                  <p className="whitespace-pre-wrap text-[var(--text-sec)]">{parent.vision?.trim() || '—'}</p>
                </div>
                <div>
                  <p className={LABEL_MONO}>Methods</p>
                  {parentTree.methods.length ? (
                    <ol className="list-decimal space-y-1 pl-5 text-[var(--text-sec)]">
                      {parentTree.methods.map((g) => <li key={g.method.id}>{g.method.title}</li>)}
                    </ol>
                  ) : (
                    <p className="text-[var(--text-ter)]">None yet.</p>
                  )}
                </div>
              </div>
            </Panel>
          )}

          <Panel label="Cascade" title="Written against this one">
            <div className="space-y-4 text-sm">
              {children.length === 0 && asksFromHere.length === 0 && (
                <p className="text-[var(--text-ter)]">
                  {v2mom.status === 'published' || v2mom.status === 'locked'
                    ? 'Nobody is writing against this one yet.'
                    : 'Once this is published, ask the people below you to write theirs against it.'}
                </p>
              )}
              {children.length > 0 && (
                <ul className="space-y-1.5">
                  {children.map((d) => {
                    const cd = dueState(d.dueOn);
                    return (
                      <li key={d.id} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5">
                        <Link to={`/prioritization/strategy/v2mom/${d.id}`} className="min-w-0 truncate text-[var(--text-pri)] hover:underline">
                          {d.title}
                        </Link>
                        <span className="inline-flex items-center gap-2">
                          <span className="text-xs text-[var(--text-ter)]">{nameOf(d.ownerId)}</span>
                          {cd && d.status === 'draft' && <span {...statusReadout(cd.overdue ? 'coral' : 'neutral')}>{cd.label}</span>}
                          <span {...statusReadout(STATUS_TONE[d.status])}>{V2MOM_STATUS_LABEL[d.status]}</span>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
              {asksFromHere.filter((a) => a.status === 'open' && !children.some((c) => c.id === a.v2momId)).length > 0 && (
                <div className="space-y-1">
                  <p className={LABEL_MONO}>Asked, not started</p>
                  <ul className="space-y-1.5">
                    {asksFromHere
                      .filter((a) => a.status === 'open' && !children.some((c) => c.id === a.v2momId))
                      .map((a) => {
                        const ad = dueState(a.dueOn);
                        return (
                          <li key={a.id} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5">
                            <span className="text-[var(--text-pri)]">{nameOf(a.askedUserId)}</span>
                            <span className="inline-flex items-center gap-2">
                              {ad && <span {...statusReadout(ad.overdue ? 'coral' : 'neutral')}>{ad.label}</span>}
                              {standing?.canManage && (
                                <button {...btnSecondary('h-8 px-2.5')} onClick={() => void withdrawAsk(a.id).then((r) => r.error && setSaveError(r.error))}>
                                  Withdraw
                                </button>
                              )}
                            </span>
                          </li>
                        );
                      })}
                  </ul>
                </div>
              )}
              {mayAsk && (
                <button {...btnSecondary()} onClick={() => setAsking(true)}>
                  <Send className="h-3.5 w-3.5" /> Ask people to write theirs
                </button>
              )}
              {!mayAsk && standing?.canManage && v2mom.status !== 'published' && v2mom.status !== 'locked' && (
                <p className="text-xs text-[var(--text-ter)]">Publish this V2MOM first; then you can ask people to write theirs against it.</p>
              )}
            </div>
          </Panel>

          {fill && (
            <Panel label="Completeness" title="What is still missing">
              <div className="space-y-3">
                <div className="flex items-center gap-1.5">
                  {V2MOM_SECTIONS.map((s) => (
                    <div key={s.key} title={s.label} className={`h-1.5 flex-1 ${fill[s.key] ? 'bg-[var(--slate)]' : 'bg-[var(--hover-tint)]'}`} />
                  ))}
                </div>
                <ul className="space-y-1 text-sm">
                  {V2MOM_SECTIONS.map((s) => (
                    <li key={s.key} className="flex items-center justify-between gap-2">
                      <span className={fill[s.key] ? 'text-[var(--text-sec)]' : 'text-[var(--text-pri)]'}>{s.label}</span>
                      <span {...statusReadout(fill[s.key] ? 'green' : 'neutral')}>{fill[s.key] ? 'Filled' : 'Empty'}</span>
                    </li>
                  ))}
                </ul>
                {missingMeasures.length > 0 && (
                  <Callout tone="coral" label="Unmeasured">
                    {missingMeasures.length === 1
                      ? `"${missingMeasures[0].title}" has no measure.`
                      : `${missingMeasures.length} methods have no measure.`}
                  </Callout>
                )}
              </div>
            </Panel>
          )}

          <Panel label="History" title="What happened" count={events.length}>
            {events.length === 0 ? (
              <p className="text-sm text-[var(--text-ter)]">Nothing yet.</p>
            ) : (
              <ol className="space-y-2 text-sm">
                {events.map((e) => (
                  <li key={e.id} className="border-l-2 pl-3" style={{ borderColor: 'var(--rule)' }}>
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                      <span className="text-[var(--text-pri)]">{V2MOM_EVENT_LABEL[e.event] ?? e.event}</span>
                      <span className="text-xs text-[var(--text-ter)]">{formatDateTime(e.createdAt)}</span>
                    </div>
                    <p className="text-xs text-[var(--text-ter)]">
                      {nameOf(e.actorId)}
                      {e.fromStatus && e.toStatus && e.fromStatus !== e.toStatus
                        ? ` · ${V2MOM_STATUS_LABEL[e.fromStatus as V2momStatus] ?? e.fromStatus} → ${V2MOM_STATUS_LABEL[e.toStatus as V2momStatus] ?? e.toStatus}`
                        : ''}
                    </p>
                    {e.note && <p className="mt-0.5 whitespace-pre-wrap text-[var(--text-sec)]">{e.note}</p>}
                  </li>
                ))}
              </ol>
            )}
          </Panel>
        </div>
      </div>

      {noteFor && (
        <ActDialog action={noteFor} busy={actBusy} onConfirm={(note) => void perform(noteFor, note || null)} onClose={() => setNoteFor(null)} />
      )}
      {asking && (
        <AskDialog parent={v2mom} members={activeMembers} openAskedIds={openAskedIds} onClose={() => setAsking(false)} />
      )}
    </div>
  );
}

export default V2momEditor;
